import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ChevronLeftIcon } from '@/assets/icon/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icon/ChevronRightIcon';
import searchHistoryIcon from '@/assets/images/search-history.png';
import { inlineNavigationStyle } from '@/components/atoms/NavigationButton/Navigation-button.contants';
import NavigationButton from '@/components/atoms/NavigationButton/NavigationButton';
import Tag from '@/components/atoms/SearchTag/SearchTag';
import { SearchHistoryProps } from '@/types';
import {
  clearSearchHistory,
  getSearchHistory,
  removeFromSearchHistory,
} from '@/utils/searchHistory';

import SearchSectionHeader from '../SearchSectionHeader/SearchSectionHeader';
import {
  NoSearchHistory,
  NoSearchHistoryText,
  SearchHistoryContainer,
  SearchHistoryTag,
  SearchHistoryTagWrapper,
} from './SearchHistory.style';

const SearchHistory: React.FC<SearchHistoryProps> = ({
  isNative,
  onClickHistory,
  isWebMobileView = false,
}) => {
  const { t } = useTranslation();
  const [historyItems, setHistoryItems] = useState(getSearchHistory());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollPosition = useCallback(() => {
    if (!isNative && scrollContainerRef?.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft) < scrollWidth - clientWidth);
    }
  }, [isNative]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkScrollPosition();

    const resizeObserver = new ResizeObserver(() => checkScrollPosition());
    resizeObserver.observe(container);
    window.addEventListener('resize', checkScrollPosition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkScrollPosition);
    };
  }, [historyItems, checkScrollPosition]);

  // Refresh history when component mounts or when storage changes
  useEffect(() => {
    const handleStorageChange = () => {
      setHistoryItems(getSearchHistory());
    };

    // Listen for storage events (from other tabs/windows)
    window.addEventListener('storage', handleStorageChange);

    // Initial load
    handleStorageChange();

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  const handleKeywordClick = (keyword: string, index: number) => {
    onClickHistory(keyword, index);
  };

  const handleClearKeyword = (keyword: string) => {
    removeFromSearchHistory(keyword);
    setHistoryItems(getSearchHistory());
  };

  const handleClearAll = () => {
    clearSearchHistory();
    setHistoryItems([]);
  };

  const hasHistory = historyItems.length > 0;

  return (
    <SearchHistoryContainer>
      <SearchSectionHeader
        title={t('search.searchHistory')}
        rightHeaderText={hasHistory ? t('global.clear') : undefined}
        rightHeaderHandler={handleClearAll}
        titleIcon={searchHistoryIcon.src}
      />
      {!hasHistory && (
        <NoSearchHistory>
          <NoSearchHistoryText>
            {t('search.noSearchHistory')}
          </NoSearchHistoryText>
        </NoSearchHistory>
      )}
      {hasHistory && (
        <SearchHistoryTagWrapper>
          {!isNative && !isWebMobileView && (
            <NavigationButton
              position="left"
              onClick={scrollLeft}
              aria-label="Scroll left"
              style={inlineNavigationStyle(canScrollLeft)}
            >
              <ChevronLeftIcon width={32} height={32} />
            </NavigationButton>
          )}

          <SearchHistoryTag
            ref={scrollContainerRef}
            onScroll={checkScrollPosition}
          >
            {historyItems?.map((item, index) => (
              <Tag
                key={`${item.keyword}-${item.timestamp}`}
                label={item.keyword}
                onClick={() => handleKeywordClick(item.keyword, index)}
                onClear={() => handleClearKeyword(item.keyword)}
                showClearIcon={true}
              />
            ))}
          </SearchHistoryTag>
          {!isNative && !isWebMobileView && (
            <NavigationButton
              position="right"
              onClick={scrollRight}
              aria-label="Scroll right"
              style={inlineNavigationStyle(canScrollRight)}
            >
              <ChevronRightIcon width={32} height={32} />
            </NavigationButton>
          )}
        </SearchHistoryTagWrapper>
      )}
    </SearchHistoryContainer>
  );
};

export default SearchHistory;
